import React, { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PageBanner from '../components/PageBanner';
import Footer from '../components/Footer';
import { submitApplication } from '../lib/api';
import { IMAGES } from '../mock';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const LEVELS = [
  'Business Member - 1 to 3 Employees — $275',
  'Business Member - 4 to 9 Employees — $350',
  'Business Member - 10 to 20 Employees — $475',
  'Business Member - 21 and over Employees — $500',
  'Non-profit and Community Member — $100',
];

const PRICING = [
  { label: '1 to 3 Employees', price: '$275' },
  { label: '4 to 9 Employees', price: '$350' },
  { label: '10 to 20 Employees', price: '$475' },
  { label: '21 and over Employees', price: '$500' },
  { label: 'Non-profit & Community', price: '$100' },
];

const BENEFITS = [
  'A voice on issues affecting local business',
  'Listing in our online business directory',
  'Networking events and member mixers',
  'Promotion through Chamber channels',
  'Member-to-member discounts',
  'Advocacy to all levels of government',
  'Business referrals from the Chamber',
  'Community & economic development support',
];

export default function MembershipPage() {
  const [form, setForm] = useState({
    business_name: '', contact_name: '', email: '', phone: '', website: '', membership_level: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email) {
      toast.error('Please fill in business name, contact name and email.');
      return;
    }
    setSubmitting(true);
    try {
      await submitApplication(form);
      setDone(true);
      toast.success('Application submitted! We will be in touch shortly.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full h-13 px-4 py-3.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[15px] focus:outline-none focus:border-[#4a9cd4] transition-colors';

  return (
    <>
      <PageBanner
        title="Join the Chamber"
        crumb="MEMBERSHIP"
        subtitle="Explore the possibilities by taking advantage of the benefits of a Chamber Membership and building effective business relationships."
        image={IMAGES.joinImg}
      />

      <section className="bg-[#f8f6f2] py-16 md:py-24">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-start">
          {/* Benefits */}
          <div>
            <p className="font-sans-ah tracking-nav text-[12px] text-[#4a9cd4] font-500 mb-4">THE BENEFITS</p>
            <h2 className="font-serif-ah text-[38px] md:text-[48px] text-[#2f2f2f] leading-tight mb-6">Why become a member?</h2>
            <p className="font-sans-ah text-[16px] text-[#555] font-300 leading-relaxed mb-8">
              Join today and let your voice be heard. Chamber membership connects your business to a thriving regional network and a team that works on your behalf.
            </p>
            <ul className="space-y-4">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#4a9cd4] flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={14} className="text-white" />
                  </span>
                  <span className="font-sans-ah text-[15px] text-[#3a3a3a]">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 bg-white rounded-sm border border-neutral-100 p-6 shadow-[0_6px_30px_rgba(0,0,0,0.05)]">
              <h3 className="font-sans-ah tracking-nav text-[12px] text-[#4a9cd4] font-500 mb-5">MEMBERSHIP LEVELS &amp; PRICING</h3>
              <ul className="divide-y divide-neutral-100">
                {PRICING.map((p) => (
                  <li key={p.label} className="flex items-center justify-between py-3">
                    <span className="font-sans-ah text-[14px] text-[#3a3a3a]">{p.label}</span>
                    <span className="font-serif-ah text-[22px] text-[#3d7ca6]">{p.price}</span>
                  </li>
                ))}
              </ul>
              <p className="font-sans-ah text-[12px] text-[#999] font-300 mt-4">Annual membership fee. Applicable taxes may apply.</p>
              <div className="mt-4 pt-4 border-t border-neutral-100 space-y-1.5">
                <p className="font-sans-ah text-[13px] text-[#555]">Subscription period: <span className="text-[#3a3a3a] font-500">1 year, on: January 15th</span></p>
                <p className="font-sans-ah text-[13px] text-[#555]">No automatically recurring payments</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-sm p-8 md:p-10 shadow-[0_10px_50px_rgba(0,0,0,0.07)] border border-neutral-100">
            {done ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 rounded-full bg-[#eef4f8] flex items-center justify-center mx-auto mb-6">
                  <Check size={38} className="text-[#4a9cd4]" />
                </div>
                <h3 className="font-serif-ah text-[30px] text-[#2f2f2f] mb-3">Thank you!</h3>
                <p className="font-sans-ah text-[15px] text-[#666] font-300">Your membership application has been received. A member of our team will reach out to you soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <h3 className="font-serif-ah text-[28px] text-[#2f2f2f] mb-2">Membership Application</h3>
                <div>
                  <label htmlFor="mem-business" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Business Name *</label>
                  <input id="mem-business" name="business_name" data-testid="membership-business-input" className={inputCls} value={form.business_name} onChange={set('business_name')} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="mem-contact" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Contact Name *</label>
                    <input id="mem-contact" name="contact_name" data-testid="membership-contact-input" className={inputCls} value={form.contact_name} onChange={set('contact_name')} />
                  </div>
                  <div>
                    <label htmlFor="mem-email" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Email *</label>
                    <input id="mem-email" name="email" data-testid="membership-email-input" type="email" className={inputCls} value={form.email} onChange={set('email')} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="mem-phone" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Phone</label>
                    <input id="mem-phone" name="phone" data-testid="membership-phone-input" className={inputCls} value={form.phone} onChange={set('phone')} />
                  </div>
                  <div>
                    <label htmlFor="mem-website" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Website</label>
                    <input id="mem-website" name="website" data-testid="membership-website-input" className={inputCls} value={form.website} onChange={set('website')} />
                  </div>
                </div>
                <div>
                  <label htmlFor="mem-level" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Membership Level</label>
                  <Select value={form.membership_level} onValueChange={(v) => setForm((f) => ({ ...f, membership_level: v }))}>
                    <SelectTrigger id="mem-level" data-testid="membership-level-trigger" className="h-13 py-3.5 bg-white border-neutral-200 rounded-sm font-sans-ah text-[15px] mt-1">
                      <SelectValue placeholder="Select a level..." />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label htmlFor="mem-message" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Tell us about your business</label>
                  <textarea id="mem-message" name="message" data-testid="membership-message-input" rows={4} className={inputCls + ' h-auto resize-none'} value={form.message} onChange={set('message')} />
                </div>
                <button type="submit" data-testid="membership-submit-btn" disabled={submitting} className="btn-ah-solid w-full font-sans-ah text-[13px] py-4 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  {submitting ? 'SUBMITTING...' : 'SUBMIT APPLICATION'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
